import { addDays, differenceInHours } from "date-fns"

type UnknownObject = Record<never, unknown>

export type UnixTS = number

export interface OpeningTime {
  hour: number
  minute: number
}

function isOpeningTime(a: UnknownObject): a is OpeningTime {
  return (
    typeof (a as OpeningTime).hour === "number" &&
    typeof (a as OpeningTime).minute === "number"
  )
}

export interface Open {
  tag: "open"
  open: OpeningTime
  close: OpeningTime
}

export function Open(a: { open: OpeningTime; close: OpeningTime }): Open {
  return {
    tag: "open",
    open: a.open,
    close: a.close,
  }
}

function isOpen(a: UnknownObject): a is Open {
  const b = a as Open
  return b.tag === "open" && isOpeningTime(b.open) && isOpeningTime(b.close)
}

interface Closed {
  tag: "closed"
}

const Closed: Closed = {
  tag: "closed",
}

function isClosed(a: UnknownObject): a is Closed {
  return (a as Closed).tag === "closed"
}

export type OpeningTimes = Open | Closed

function isOpeningTimes(a: UnknownObject): a is OpeningTimes {
  return isOpen(a) || isClosed(a)
}

interface ChangedHours {
  tag: "changedHours"
  hours: OpeningTimes
  reason?: string
}

export function ChangedHours(
  hours: OpeningTimes,
  reason?: string
): ChangedHours {
  return {
    tag: "changedHours",
    hours,
    reason,
  }
}

function isChangedHours(a: UnknownObject): a is ChangedHours {
  const b = a as ChangedHours
  return (
    b.tag === "changedHours" &&
    isOpeningTimes(b.hours) &&
    (typeof b.reason === "string" || b.reason == null)
  )
}

interface UsualHours {
  tag: "usualHours"
  hours: OpeningTimes
}

export function UsualHours(hours: OpeningTimes): UsualHours {
  return {
    tag: "usualHours",
    hours,
  }
}

function isUsualHours(a: UnknownObject): a is UsualHours {
  return (
    (a as UsualHours).tag === "usualHours" &&
    isOpeningTimes((a as UsualHours).hours)
  )
}

export type OnlineStatus = "Unknown" | "Offline" | "Online"

export type OpenStatus = UsualHours | ChangedHours

function isOpenStatus(a: UnknownObject): a is OpenStatus {
  return isUsualHours(a) || isChangedHours(a)
}

export interface UpcomingException {
  // ISO date string (YYYY-MM-DD) for the day the exception applies to.
  date: string
  openingHours: OpeningTimes
  reason: string | null
}

function isUpcomingException(a: UnknownObject): a is UpcomingException {
  const b = a as UpcomingException
  return (
    typeof b.date === "string" &&
    !Number.isNaN(Date.parse(b.date)) &&
    isOpeningTimes(b.openingHours as unknown as UnknownObject) &&
    (typeof b.reason === "string" || b.reason == null)
  )
}

export interface OpeningHours {
  monday: OpenStatus
  tuesday: OpenStatus
  wednesday: OpenStatus
  thursday: OpenStatus
  friday: OpenStatus
  saturday: OpenStatus
  sunday: OpenStatus
  upcomingExceptions: UpcomingException[]
}

export function isOpeningHours(a: UnknownObject): a is OpeningHours {
  const b: OpeningHours = a as OpeningHours
  return (
    isOpenStatus(b.monday) &&
    isOpenStatus(b.tuesday) &&
    isOpenStatus(b.wednesday) &&
    isOpenStatus(b.thursday) &&
    isOpenStatus(b.friday) &&
    isOpenStatus(b.saturday) &&
    isOpenStatus(b.sunday) &&
    Array.isArray(b.upcomingExceptions) &&
    b.upcomingExceptions.every(e => isUpcomingException(e))
  )
}

export function isAfterLastSundayOfMonth(
  month: number /*indexed from 0*/,
  now: UnixTS
): boolean {
  const sunday = 0
  const oneDay = 24 * 60 * 60 * 1000

  const noSundayInMonthFromDate = (currentTime: number): boolean => {
    const utcCurrentTime = new Date(+currentTime)
    const nextDay = currentTime + oneDay
    return (
      utcCurrentTime.getUTCMonth() !== month ||
      (utcCurrentTime.getUTCDay() !== sunday &&
        noSundayInMonthFromDate(nextDay))
    )
  }

  const nowDay = new Date(now).getUTCDay() // 0 = Sunday

  const laterThanTimeChange = (currentTime: UnixTS): boolean => {
    // Time changes on the last Sunday at 1:00:00 UTC
    const timeChangeHour = 1
    const timeChangeMinute = 0
    const timeChangeSecond = 0

    const utcNowHour = new Date(currentTime).getUTCHours()
    const utcNowMinute = new Date(currentTime).getUTCMinutes()
    const utcNowSecond = new Date(currentTime).getUTCSeconds()

    return (
      utcNowHour >= timeChangeHour &&
      utcNowMinute >= timeChangeMinute &&
      utcNowSecond >= timeChangeSecond
    )
  }

  return (
    noSundayInMonthFromDate(now + oneDay) &&
    (nowDay !== sunday || laterThanTimeChange(now))
  )
}

export function isBST(now: UnixTS): boolean {
  // BST begins at 01:00 GMT on the last Sunday of March and
  // ends at 01:00 GMT (02:00 BST) on the last Sunday of October
  const march = 2
  const april = 3
  const september = 8
  const october = 9
  const utcNowMonth = new Date(now).getUTCMonth()
  const isInMarchBST =
    march === utcNowMonth && isAfterLastSundayOfMonth(march, now)
  const isInOctoberBST =
    october === utcNowMonth && !isAfterLastSundayOfMonth(october, now)
  return (
    isInMarchBST ||
    (april <= utcNowMonth && utcNowMonth <= september) ||
    isInOctoberBST
  )
}

function statusForDay(hours: OpeningHours, time: UnixTS): OpenStatus {
  const localNow = new Date(time)
  const nowDayUTC = localNow.getUTCDay()
  switch (nowDayUTC) {
    case 0:
      return hours.sunday
    case 1:
      return hours.monday
    case 2:
      return hours.tuesday
    case 3:
      return hours.wednesday
    case 4:
      return hours.thursday
    case 5:
      return hours.friday
    case 6:
      return hours.saturday
    default:
      throw new Error("statusForDay unrecognized day " + nowDayUTC)
  }
}

// An upcoming exception for the day overrides that weekday's usual hours.
function hoursForDay(hours: OpeningHours, time: UnixTS): OpeningTimes {
  const isoDate = new Date(time).toISOString().slice(0, 10)
  const exception = hours.upcomingExceptions.find(e => e.date === isoDate)
  return exception ? exception.openingHours : statusForDay(hours, time).hours
}

export const getHourDifferenceUntilOpened = (
  openingHours: OpeningHours
): number | undefined => {
  const oneHourMs = 60 * 60 * 1000

  const date = new Date()
  const now = +date // Unix TS
  const nowPlusADay = now + 24 * oneHourMs

  // If summer shift UTC +1h, otherwise plain GMT
  // UK local time now, expressed in Unix TS
  const ukNow = isBST(now) ? now + oneHourMs : now
  const ukNowPlusADay = isBST(nowPlusADay)
    ? nowPlusADay + oneHourMs
    : nowPlusADay
  const localNow = new Date(ukNow)

  const todayStatus = statusForDay(openingHours, ukNow)
  const tomorrowStatus = statusForDay(openingHours, ukNowPlusADay)

  const todaysDate = (openHour: number): number =>
    Date.UTC(
      localNow.getUTCFullYear(),
      localNow.getMonth(),
      localNow.getDate(),
      openHour
    )

  const tomorrowDate = (openHour: number): number =>
    Date.UTC(
      localNow.getUTCFullYear(),
      localNow.getMonth(),
      addDays(localNow, 1).getDate(),
      openHour
    )

  if (
    todayStatus.hours.tag === "open" &&
    localNow.getHours() <= todayStatus.hours.open.hour
  ) {
    return (
      differenceInHours(todaysDate(todayStatus.hours.open.hour), localNow) + 1
    )
  }

  if (tomorrowStatus.hours.tag === "open") {
    return (
      differenceInHours(
        tomorrowDate(tomorrowStatus.hours.open.hour),
        localNow
      ) + 1
    )
  }

  return undefined
}

export function isTimeStampInOpeningHoursRange(
  openingHours: OpeningHours,
  now: UnixTS
): boolean {
  const localNow = new Date(now)
  const nowHourUTC = localNow.getUTCHours()
  const nowMinuteUTC = localNow.getUTCMinutes()
  const nowSecondUTC = localNow.getUTCSeconds()

  // Date is not relevant, we just need some point in time to compare everything
  const comparisonNow = Date.UTC(
    2019,
    5,
    5,
    nowHourUTC,
    nowMinuteUTC,
    nowSecondUTC
  )
  const comparisonOpen = (openHour: number, openMinute: number): number =>
    Date.UTC(2019, 5, 5, openHour, openMinute)
  const comparisonClose = (closeHour: number, closeMinute: number): number =>
    Date.UTC(2019, 5, 5, closeHour, closeMinute)

  const hours = hoursForDay(openingHours, now)
  switch (hours.tag) {
    case "open":
      return (
        comparisonOpen(hours.open.hour, hours.open.minute) <= comparisonNow &&
        comparisonNow < comparisonClose(hours.close.hour, hours.close.minute)
      )
    case "closed":
      return false
  }
}

export function getMortgageExpertsOnlineStatus(
  openingHours: OpeningHours,
  localNow: Date
): OnlineStatus {
  const now = +localNow // Unix TS
  const oneHourMs = 60 * 60 * 1000
  // If summer shift UTC +1h, otherwise plain GMT
  // UK local time now, expressed in Unix TS
  const ukNow = isBST(now) ? now + oneHourMs : now
  return isTimeStampInOpeningHoursRange(openingHours, ukNow)
    ? "Online"
    : "Offline"
}
